import React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Loader2, AlertCircle, Home, Truck, Gift } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function RegisterPage() {
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // v23.14 — driver-to-driver referral code from share URL `?ref=CODE`
  const refFromUrl = (searchParams.get("ref") || "").trim().toUpperCase();
  const [role, setRole] = React.useState(refFromUrl ? "driver" : "customer");
  const [driverType, setDriverType] = React.useState("self_employed");
  const [form, setForm] = React.useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    referralCode: refFromUrl,
  });
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (user && typeof user === "object") {
      // Newly-registered users go to email verification first if not yet verified
      if (user.email_verified === false) {
        navigate("/verify-email", { replace: true });
      } else {
        navigate(`/dashboard/${user.role}`, { replace: true });
      }
    }
  }, [user, navigate]);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    const res = await register({
      name: form.name.trim(),
      email: form.email.trim().toLowerCase(),
      phone: form.phone.trim() || null,
      password: form.password,
      role,
      driver_type: role === "driver" ? driverType : undefined,
      referral_code: role === "driver" && driverType === "self_employed" && form.referralCode ? form.referralCode.trim().toUpperCase() : undefined,
    });
    setLoading(false);
    if (res.ok) {
      // New signups → email verification first; existing flow otherwise
      if (res.user?.email_verified === false) {
        navigate("/verify-email", { replace: true });
      } else {
        navigate(`/dashboard/${res.user.role}`, { replace: true });
      }
    } else {
      setError(res.error || "Registration failed");
    }
  };

  return (
    <div className="min-h-screen flex items-stretch bg-white" data-testid="register-page">
      {/* Left visual */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#1E4D55] text-white relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <Link
            to="/"
            className="flex items-center font-display font-black tracking-tighter text-white"
          >
            <span className="inline-block w-2.5 h-2.5 bg-[#5BB247]" />
            <span className="text-2xl ml-2">raanah<span className="text-[#5BB247]">.</span></span>
          </Link>
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-white/70">
              Join the platform
            </p>
            <h1 className="font-display mt-4 text-4xl xl:text-5xl tracking-tighter font-black leading-[1]">
              {role === "driver" ? (
                <>
                  Earn £600–£1,200 / day
                  <br />
                  <span className="text-[#5BB247]">on premium jobs.</span>
                </>
              ) : (
                <>
                  Book a vetted Luton van
                  <br />
                  <span className="text-[#5BB247]">in 60 seconds.</span>
                </>
              )}
            </h1>
            <p className="mt-6 text-white/70 max-w-md leading-relaxed">
              {role === "driver"
                ? "Sign up, upload your docs, get verified, and start earning from house removals, waste clearance and office moves across the UK."
                : "Post your move, get matched with a vetted driver, and pay safely via escrow — released only on proof of completion."}
            </p>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center px-6 md:px-12 py-12 bg-[#F5F5F5]">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="w-full max-w-md"
        >
          <Link
            to="/"
            className="lg:hidden inline-flex items-center font-display font-black tracking-tighter text-[#0A0A0A] mb-8"
          >
            <span className="inline-block w-2.5 h-2.5 bg-[#1E4D55]" />
            <span className="text-2xl ml-2">raanah<span className="text-[#1E4D55]">.</span></span>
          </Link>

          <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1E4D55]">
            Create account
          </p>
          <h2 className="font-display mt-2 text-4xl tracking-tighter font-black text-[#0A0A0A]">
            Let&apos;s get you in.
          </h2>
          <p className="mt-3 text-[#475569]">
            Already have one?{" "}
            <Link
              to="/login"
              data-testid="register-login-link"
              className="text-[#1E4D55] font-semibold underline underline-offset-4 hover:text-[#163C42]"
            >
              Sign in
            </Link>
          </p>

          {/* Role selector */}
          <div className="mt-8 grid grid-cols-2 gap-3" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={role === "customer"}
              data-testid="register-role-customer"
              onClick={() => setRole("customer")}
              className={`flex items-center gap-3 px-4 py-4 border text-left transition-colors ${
                role === "customer"
                  ? "bg-[#1E4D55] text-white border-[#1E4D55]"
                  : "bg-white text-[#0A0A0A] border-[#0A0A0A]/15 hover:border-[#1E4D55]/40"
              }`}
            >
              <Home size={18} />
              <div>
                <p className="text-xs uppercase tracking-[0.14em] font-bold">Customer</p>
                <p className="text-[10px] opacity-75">Book a move</p>
              </div>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={role === "driver"}
              data-testid="register-role-driver"
              onClick={() => setRole("driver")}
              className={`flex items-center gap-3 px-4 py-4 border text-left transition-colors ${
                role === "driver"
                  ? "bg-[#1E4D55] text-white border-[#1E4D55]"
                  : "bg-white text-[#0A0A0A] border-[#0A0A0A]/15 hover:border-[#1E4D55]/40"
              }`}
            >
              <Truck size={18} />
              <div>
                <p className="text-xs uppercase tracking-[0.14em] font-bold">Driver</p>
                <p className="text-[10px] opacity-75">Earn weekly</p>
              </div>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="register-form">
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-2">
                Full name
              </label>
              <input
                type="text"
                required
                value={form.name}
                onChange={update("name")}
                placeholder={role === "driver" ? "Daniel Roberts" : "Aisha Mitchell"}
                data-testid="register-name-input"
                className="w-full px-4 py-3.5 bg-white border border-[#0A0A0A]/15 focus:outline-none focus:border-[#1E4D55] focus:ring-2 focus:ring-[#1E4D55]/15 text-sm"
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-2">
                Email
              </label>
              <input
                type="email"
                required
                value={form.email}
                onChange={update("email")}
                placeholder="you@example.com"
                data-testid="register-email-input"
                className="w-full px-4 py-3.5 bg-white border border-[#0A0A0A]/15 focus:outline-none focus:border-[#1E4D55] focus:ring-2 focus:ring-[#1E4D55]/15 text-sm"
              />
            </div>

            {role === "driver" && (
              <div data-testid="register-driver-type-block">
                <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-2">
                  Driver type
                </label>
                <select
                  value={driverType}
                  onChange={(e) => setDriverType(e.target.value)}
                  data-testid="register-driver-type-select"
                  className="w-full px-4 py-3.5 bg-white border border-[#0A0A0A]/15 focus:outline-none focus:border-[#1E4D55] focus:ring-2 focus:ring-[#1E4D55]/15 text-sm"
                >
                  <option value="self_employed">Self-employed — I own my van and take marketplace jobs</option>
                  <option value="managed">Managed by Raanah — I&apos;m on the Raanah team</option>
                </select>
                <p className="mt-1.5 text-[11px] text-[#475569] leading-relaxed" data-testid="register-driver-type-hint">
                  {driverType === "managed"
                    ? "You'll skip document uploads. After signing in you'll only see your Raanah ID card and shift clocking tool."
                    : "You'll upload your licence, V5C and insurance after signing in so we can verify you."}
                </p>
              </div>
            )}

            {role === "driver" && (
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-2">
                  Mobile (for WhatsApp alerts)
                </label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="+44 7700 900123"
                  data-testid="register-phone-input"
                  className="w-full px-4 py-3.5 bg-white border border-[#0A0A0A]/15 focus:outline-none focus:border-[#1E4D55] focus:ring-2 focus:ring-[#1E4D55]/15 text-sm"
                />
              </div>
            )}

            {/* v23.14 — Driver-to-driver referral code (optional, self-employed drivers only) */}
            {role === "driver" && driverType === "self_employed" && (
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-2 inline-flex items-center gap-1.5">
                  <Gift size={11} /> Referral code <span className="opacity-50 normal-case tracking-normal font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={form.referralCode}
                  onChange={(e) => setForm((f) => ({ ...f, referralCode: e.target.value.toUpperCase() }))}
                  placeholder="DRVR-1A2B"
                  data-testid="register-referral-code-input"
                  className="w-full px-4 py-3.5 bg-white border border-[#0A0A0A]/15 focus:outline-none focus:border-[#1E4D55] focus:ring-2 focus:ring-[#1E4D55]/15 text-sm font-mono uppercase"
                />
                {refFromUrl && (
                  <p data-testid="register-referral-applied" className="mt-1.5 text-[11px] text-[#2c8642] inline-flex items-center gap-1"><Gift size={10} /> Referral code applied — you both get £25 off Pro after verification.</p>
                )}
              </div>
            )}

            <div>
              <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-2">
                Password
              </label>
              <input
                type="password"
                required
                minLength={8}
                value={form.password}
                onChange={update("password")}
                placeholder="At least 8 characters"
                data-testid="register-password-input"
                className="w-full px-4 py-3.5 bg-white border border-[#0A0A0A]/15 focus:outline-none focus:border-[#1E4D55] focus:ring-2 focus:ring-[#1E4D55]/15 text-sm"
              />
            </div>

            {error && (
              <div
                data-testid="register-error"
                className="flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3"
              >
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="register-submit-button"
              className="group w-full inline-flex items-center justify-center gap-2 bg-[#1E4D55] text-white hover:bg-[#163C42] transition-colors px-6 py-4 font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  <span>Creating account…</span>
                </>
              ) : (
                <>
                  <span>{role === "driver" ? "Apply to drive" : "Create account"}</span>
                  <ArrowUpRight
                    size={18}
                    className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </>
              )}
            </button>
          </form>

          <p className="mt-6 text-xs text-[#475569] leading-relaxed">
            By creating an account you agree to our terms &amp; privacy policy.
            {role === "driver" && driverType === "self_employed" && " You'll be asked to upload your licence, V5C and insurance after signing in."}
            {role === "driver" && driverType === "managed" && " You'll get access to your Raanah ID card and shift clocking tool as soon as your email is verified."}
          </p>
        </motion.div>
      </div>
    </div>
  );
}
